import { useMemo } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack } from "@mui/material";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import { AccountTransactionsSection } from "./account-transactions-section";
import { GeneralInformationSection } from "./general-information-section";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./journal-voucher-form-constants";

export const JournalVoucherForm = ({
  defaultValues = DEFAULT_VALUES,
  onSubmit,
  renderActionButton,
}) => {
  const methods = useForm({
    defaultValues,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const { handleSubmit, control } = methods;

  // Watch transactions to check if balanced
  const transactions = useWatch({
    control,
    name: "outletChartOfAccountTransactions",
  });

  const isBalanced = useMemo(() => {
    if (!transactions) return false;

    const debit = transactions.reduce(
      (sum, t) => sum + (Number(t?.debit) || 0),
      0
    );
    const credit = transactions.reduce(
      (sum, t) => sum + (Number(t?.credit) || 0),
      0
    );

    return Math.abs(debit - credit) < 0.01 && debit > 0;
  }, [transactions]);

  const onFormSubmit = (data) => {
    // Transform data to match API payload format
    const payload = {
      outletId: data.outlet.id, // Include outlet ID for header override
      transactionDate: dayjs(data.transactionDate).format("YYYY-MM-DD"),
      note: data.note || null,
      outletChartOfAccountTransactions:
        data.outletChartOfAccountTransactions.map((transaction) => ({
          outletChartOfAccountId: transaction.outletChartOfAccount.id,
          amount:
            transaction.debit > 0 ? transaction.debit : -transaction.credit, // Positive for debit, negative for credit
          note: transaction.note || null,
          ...(transaction.entityType && {
            entityType: transaction.entityType,
            entityId: transaction.entity.id,
          }),
        })),
    };

    onSubmit(payload);
  };

  return (
    <FormProvider {...methods}>
      <Stack
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        spacing={3}
      >
        <GeneralInformationSection />

        <AccountTransactionsSection />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end"
          }}>
          {renderActionButton({ isBalanced })}
        </Box>
      </Stack>
    </FormProvider>
  );
};

JournalVoucherForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  renderActionButton: PropTypes.func.isRequired,
};
